import { useLayoutEffect, useRef, useState } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";

/**
 * Building blocks of the mission sheet, ported from the temp worker app (jobbiz_app_v2,
 * features/missions and components/ui): sections split by a thin rule, info rows with a bordered
 * icon tile, and a description folded past eight lines.
 */

/**
 * All the vertical rhythm comes from the divider, as in the app: 60px for a 1px rule. Sections
 * have no margin of their own, which keeps their gaps even.
 */
export const MissionSectionDivider = () => (
    <div className="flex h-[60px] items-center" aria-hidden="true">
        <div className="h-px w-full bg-gray-100" />
    </div>
);

export const MissionSection = ({ title, children, divided = true }) => (
    <>
        <section>
            {title ? <h2 className="mb-1 text-sm font-semibold leading-5 text-gray-900">{title}</h2> : null}
            {children}
        </section>
        {divided ? <MissionSectionDivider /> : null}
    </>
);

/** Body text of a section: 14px, gray-500, as the app `body` variant. */
export const MissionText = ({ children, className = "" }) => (
    <p className={`text-sm leading-5 text-gray-500 ${className}`}>{children}</p>
);

/**
 * An info line: white bordered tile with its icon, a label and a value. `action` sits at the end
 * of the line (a rating, an edit link).
 */
export const InfoRow = ({ icon: Icon, label, value, action }) => (
    <div className="flex items-center gap-3">
        <div className="corner-smooth-12 flex h-[42px] w-[42px] shrink-0 items-center justify-center border border-gray-100 bg-white">
            <Icon size={20} color="#374151" strokeWidth={2} aria-hidden="true" />
        </div>
        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
            <p className="text-sm font-semibold leading-5 text-gray-900">{label}</p>
            {value ? <p className="text-sm leading-5 text-gray-500">{value}</p> : null}
        </div>
        {action ? <div className="shrink-0">{action}</div> : null}
    </div>
);

/** Lines of description past which the text folds, as in the app. */
const FOLDED_LINES = 8;
const LINE_HEIGHT = 20;

/**
 * The job description (HTML written in the back office), folded past eight lines with
 * "Voir plus". The overflow is measured on the full content, so the button never shows on a text
 * that fits exactly.
 */
export const MissionDescription = ({ title, html, seeMoreLabel, seeLessLabel }) => {
    const contentRef = useRef(null);
    const [expanded, setExpanded] = useState(false);
    const [overflowing, setOverflowing] = useState(false);

    useLayoutEffect(() => {
        const node = contentRef.current;
        if (node) setOverflowing(node.scrollHeight > FOLDED_LINES * LINE_HEIGHT + 1);
    }, [html]);

    return (
        <MissionSection title={title}>
            <div
                ref={contentRef}
                className="mission-description overflow-hidden text-sm leading-5 text-gray-500"
                style={expanded ? undefined : { maxHeight: FOLDED_LINES * LINE_HEIGHT }}
                dangerouslySetInnerHTML={{ __html: html }}
            />
            {overflowing ? (
                <button
                    type="button"
                    onClick={() => setExpanded((value) => !value)}
                    className="mt-4 inline-flex items-center gap-1.5 rounded-full border border-gray-200 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 active:bg-gray-100"
                >
                    {expanded ? seeLessLabel : seeMoreLabel}
                    {expanded ? <ChevronUp size={16} aria-hidden="true" /> : <ChevronDown size={16} aria-hidden="true" />}
                </button>
            ) : null}
        </MissionSection>
    );
};
